import type { FC, ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';

import { AriaLiveMessage } from '@xing-com/crate-jobs-components-aria-live-message';

import { WizardActions } from './components/wizard-actions';
import { WizardProgressBar } from './components/wizard-progress-bar';
import { WizardReview } from './components/wizard-review';
import type { Step, ExternalDocsLegacySupportProp } from './types';
import {
  HeaderContainer,
  StepContainer,
  WizardContainer,
} from './wizard.styles';

type Props = {
  header?: ReactElement;
  banner?: ReactElement;
  steps: Step[];
  reviewStepContent?: ReactElement;
  onSubmit: () => void;
  trackingProps: {
    itemUrn: string;
    flags: string;
    page: string;
    context: string;
  };
  externalDocuments?: ExternalDocsLegacySupportProp;
  contextualConsent?: ReactElement;
  validate?: (stepIndex: number) => Promise<boolean>;
  isLoading?: boolean;
};

export const Wizard: FC<Props> = ({
  header,
  banner,
  steps,
  reviewStepContent,
  onSubmit,
  validate,
  isLoading = false,
  externalDocuments,
  trackingProps,
  contextualConsent,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isReviewMode, setIsReviewMode] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);

  const totalSteps = steps.length + 1; // +1 to account for the review step
  const hasNext = currentStep < totalSteps;
  const hasPrevious = currentStep > 1;
  const isReviewStep = currentStep === totalSteps;

  useEffect(() => {
    if (isReviewStep) setIsReviewMode(isReviewStep);
  }, [isReviewStep]);

  useEffect(() => {
    // move the focus from next/back button to the first item on the view on step change
    headerRef.current?.focus();
  }, [currentStep]);

  const goToNextStep = async (): Promise<void> => {
    const isValid = await validate?.(currentStep);
    if (hasNext && isValid) setCurrentStep((step) => step + 1);
  };

  const goToPreviousStep = (): void => {
    if (hasPrevious) setCurrentStep((step) => step - 1);
  };

  const goToReviewStep = async (): Promise<void> => {
    const isValid = await validate?.(currentStep);
    if (isValid) setCurrentStep(totalSteps);
  };

  if (isReviewStep) {
    return (
      <WizardReview
        steps={steps}
        banner={banner}
        reviewStepContent={reviewStepContent}
        isLoading={isLoading}
        onReviewStep={(step) => setCurrentStep(step)}
        onSubmit={onSubmit}
        externalDocuments={externalDocuments}
        contextualConsent={contextualConsent}
        trackingProps={trackingProps}
      />
    );
  }

  return (
    <WizardContainer>
      <AriaLiveMessage messageId={steps[currentStep - 1].name} />
      <HeaderContainer tabIndex={-1} ref={headerRef} role="group">
        {currentStep === 1 ? header : null}
        <WizardProgressBar
          currentStep={currentStep}
          totalSteps={steps.length}
          isReviewMode={isReviewMode}
        />
      </HeaderContainer>
      {banner}
      <StepContainer>{steps[currentStep - 1].content}</StepContainer>
      <WizardActions
        hasPrevious={hasPrevious}
        isReviewMode={isReviewMode}
        isLoading={isLoading}
        onNext={goToNextStep}
        onBack={goToPreviousStep}
        onReview={goToReviewStep}
      />
    </WizardContainer>
  );
};
